import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { LogOut, User, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const UserMenu = ({ onOpenSettings }) => {
    const { user, isAuthenticated, isLoading, login, logout } = useAuth();

    if (isLoading) {
        return <div className="w-8 h-8 rounded-full bg-[#1e293b] animate-pulse" />;
    }

    if (isAuthenticated && user) {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-2 h-8 rounded-md bg-[#09090b]/80 border border-[#1e293b] hover:border-[#d4af37] transition-all outline-none group leading-none">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#d4af37]/20 to-[#a16207]/20 border border-[#d4af37]/30 overflow-hidden shrink-0 pointer-events-none">
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                />
                            ) : (
                                <User className="w-3.5 h-3.5 text-[#d4af37]" />
                            )}
                        </div>
                        <span className="text-xs font-medium text-[#f8fafc] hidden sm:inline max-w-[120px] truncate pointer-events-none">
                            {user.name}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5 text-[#475569] group-hover:text-[#d4af37] transition-colors shrink-0 pointer-events-none" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-[#09090b] border-[#1e293b] text-[#f8fafc] p-1" align="end">
                    <DropdownMenuLabel className="flex flex-col px-2 py-1.5">
                        <span className="text-sm font-semibold truncate">{user.name}</span>
                        <span className="text-xs text-[#94a3b8] truncate font-normal">{user.email}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[#1e293b]" />
                    
                    <DropdownMenuItem 
                        className="flex items-center gap-2 px-2 py-2 cursor-pointer focus:bg-[#1e293b] focus:text-[#d4af37] rounded-md transition-colors"
                        onClick={onOpenSettings}
                    >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-[#1e293b]" />
                    
                    <DropdownMenuItem 
                        className="flex items-center gap-2 px-2 py-2 cursor-pointer focus:bg-[#ef4444]/10 focus:text-[#ef4444] text-[#94a3b8] rounded-md transition-colors"
                        onClick={logout}
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <div className="flex items-center justify-center h-8 w-auto overflow-hidden">
            <div className="scale-[1.05] origin-center flex items-center justify-center">
                <GoogleLogin
                    onSuccess={(credentialResponse) => {
                        login(credentialResponse.credential);
                    }}
                    onError={() => {
                        console.error('Google Login Failed');
                    }}
                    size="small"
                    theme="filled_black"
                    type="icon"
                    shape="circle"
                />
            </div>
        </div>
    );
};

export default UserMenu;
